import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MobileNav } from '../components/MobileNav';
// Closing lines for PARTIAL output
const partialClosingLines = ["You don't need to do anything with this.", "This is simply what's visible right now.", "If this felt accurate, you're free to sit with it."];
// Closing lines for GROUNDED output (stronger)
const groundedClosingLines = ['This is what this moment is asking to be understood — not solved.', 'The timing explains the tension, not a flaw.', 'Clarity here comes from seeing the phase, not escaping it.'];
export function DecodeOutput() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGrounded = searchParams.get('grounded') === 'true';
  const [userInput, setUserInput] = useState('');
  const [closingLine, setClosingLine] = useState('');
  const [birthDetails, setBirthDetails] = useState<{
    name: string;
    dateOfBirth: string;
    timeOfBirth: string;
    placeOfBirth: string;
  } | null>(null);
  useEffect(() => {
    // Get user input from session storage
    const input = sessionStorage.getItem('decodeInput');
    if (!input) {
      navigate('/decode/input');
      return;
    }
    setUserInput(input);
    // If grounded, get birth details
    if (isGrounded) {
      const name = sessionStorage.getItem('decodeName') || '';
      const dateOfBirth = sessionStorage.getItem('decodeDateOfBirth') || '';
      const timeOfBirth = sessionStorage.getItem('decodeTimeOfBirth') || '';
      const placeOfBirth = sessionStorage.getItem('decodePlaceOfBirth') || '';
      if (!dateOfBirth || !placeOfBirth) {
        // Missing required details, redirect to details page
        navigate('/decode/details');
        return;
      }
      setBirthDetails({
        name,
        dateOfBirth,
        timeOfBirth,
        placeOfBirth
      });
    }
    // Select random closing line based on state
    const lines = isGrounded ? groundedClosingLines : partialClosingLines;
    const randomIndex = Math.floor(Math.random() * lines.length);
    setClosingLine(lines[randomIndex]);
  }, [navigate, isGrounded]);
  if (!userInput) {
    return null;
  }
  // Generate reflection based on grounded state
  const reflection = isGrounded && birthDetails ? generateGroundedReflection(userInput, birthDetails) : generatePartialReflection(userInput);
  return <>
      <Helmet data-id="element-1021">
        <title data-id="element-1022">Self Decode | Lazy Monk Labs</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-1023">
        <MobileNav data-id="element-1024" />

        <main className="pt-20 sm:pt-24 pb-20 sm:pb-28" data-id="element-1025">
          <div className="max-w-2xl mx-auto px-6 sm:px-8" data-id="element-1026">
            {/* 1. WHAT YOU'RE NOTICING */}
            <section className="py-12 sm:py-16" data-id="element-1027">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-6" data-id="element-1028">
                What you're noticing
              </p>
              <div className="space-y-4 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1029">
                {reflection.noticing.map((line, index) => <p key={index} data-id="element-1030">{line}</p>)}
              </div>
            </section>

            {/* 2. THE PATTERN UNDERNEATH */}
            <section className="py-12 sm:py-16 border-t border-[#2B2B2B]" data-id="element-1031">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-6" data-id="element-1032">
                The pattern underneath
              </p>
              <div className="space-y-4 text-base sm:text-lg text-[#F5F5F5] leading-relaxed" data-id="element-1033">
                {Array.isArray(reflection.pattern) ? reflection.pattern.map((line, index) => <p key={index} data-id="element-1034">{line}</p>) : <p data-id="element-1035">{reflection.pattern}</p>}
              </div>
            </section>

            {/* 3. THE QUIET REFRAME */}
            <section className="py-12 sm:py-16 border-t border-[#2B2B2B]" data-id="element-1036">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-6" data-id="element-1037">
                The quiet reframe
              </p>
              <p className="text-base sm:text-lg text-[#F5F5F5] leading-relaxed" data-id="element-1038">
                {reflection.reframe}
              </p>
            </section>

            {/* 4. WHAT REMAINS YOUR CHOICE */}
            <section className="py-12 sm:py-16 border-t border-[#2B2B2B]" data-id="element-1039">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-6" data-id="element-1040">
                What remains your choice
              </p>
              <div className="space-y-4 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1041">
                {Array.isArray(reflection.choice) ? reflection.choice.map((line, index) => <p key={index} data-id="element-1042">{line}</p>) : <p data-id="element-1043">{reflection.choice}</p>}
              </div>
            </section>

            {/* CLOSING LINE */}
            <section className="py-16 sm:py-20 border-t border-[#2B2B2B]" data-id="element-1044">
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1045">
                {closingLine}
              </p>
            </section>

            {/* DETAILS PROMPT (only for PARTIAL output) */}
            {!isGrounded && <section className="py-16 sm:py-20 border-t border-[#2B2B2B]" data-id="element-1046">
                <div className="space-y-8" data-id="element-1047">
                  <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1048">
                    To ground this reflection more precisely in timing, you can
                    add your birth details.
                  </p>

                  <div className="space-y-4" data-id="element-1049">
                    <Link to="/decode/details" className="inline-block px-6 py-3 border border-[#2B2B2B] text-sm text-gray-400 hover:text-[#F5F5F5] hover:border-gray-500 transition-colors" data-id="element-1050">
                      Add details
                    </Link>

                    <div data-id="element-1051">
                      <button onClick={() => {}} className="text-sm text-gray-600 hover:text-gray-400 transition-colors" data-id="element-1052">
                        Skip this
                      </button>
                    </div>
                  </div>
                </div>
              </section>}
          </div>
        </main>
      </div>
    </>;
}
// PARTIAL reflection generator (pre-details)
function generatePartialReflection(input: string) {
  const words = input.toLowerCase();
  const hasWork = words.includes('work') || words.includes('job') || words.includes('career');
  const hasRelationship = words.includes('relationship') || words.includes('partner') || words.includes('friend');
  const hasDecision = words.includes('decision') || words.includes('choose') || words.includes('decide');
  const hasTime = words.includes('time') || words.includes('busy') || words.includes('waiting');
  const hasEnergy = words.includes('tired') || words.includes('energy') || words.includes('exhausted');
  let noticing: string[];
  let pattern: string;
  let reframe: string;
  let choice: string;
  if (hasWork) {
    noticing = ["Something at work isn't landing the way it used to.", "There's effort happening, but the return feels unclear.", 'The rhythm of it has shifted without a clear reason.'];
    pattern = "There's a recurring pattern where output and meaning have become disconnected. The doing continues, but the orientation has quietly drifted.";
    reframe = "This isn't burnout — it's effort without orientation.";
    choice = 'Nothing here needs to change immediately. Seeing this clearly is already the interruption.';
  } else if (hasRelationship) {
    noticing = ["Something in how you're relating has become slightly misaligned.", 'The connection is still there, but the ease has shifted.', "There's a gap between what's said and what's felt."];
    pattern = "What repeats is a tension between closeness and clarity — wanting both, but finding they don't arrive together.";
    reframe = "This isn't distance — it's unspoken precision.";
    choice = 'Whether you name this aloud or not is separate from seeing it.';
  } else if (hasDecision) {
    noticing = ['A decision is present, but not pressing.', 'Multiple directions feel equally valid.', 'The weight of choosing has become heavier than the choice itself.'];
    pattern = 'This tends to show up as suspended motion — not indecision, but a loop of re-evaluation that delays landing anywhere.';
    reframe = "This isn't indecision — it's suspended clarity.";
    choice = 'Seeing this clearly is already the interruption. Whether you act or not is separate from seeing.';
  } else if (hasTime || hasEnergy) {
    noticing = ['Time is passing, but not in the way you expected.', "There's a sense of waiting without knowing what for.", 'Energy is being spent, but not replenished in kind.'];
    pattern = "There's a recurring pattern where presence and progress feel mutually exclusive — as if being here fully would mean falling behind.";
    reframe = "This isn't stagnation — it's motion without traction.";
    choice = 'Nothing here needs to change immediately.';
  } else {
    noticing = ['Something is slightly off, but not urgently wrong.', "There's a quiet dissonance between what is and what feels right.", 'The situation is familiar, but the feeling has shifted.'];
    pattern = 'What repeats is a gap between how things appear and how they land — a loop where the surface stays stable while something underneath keeps moving.';
    reframe = "This isn't confusion — it's clarity arriving slowly.";
    choice = 'Seeing this clearly is already the interruption. Whether you act or not is separate from seeing.';
  }
  return {
    noticing,
    pattern,
    reframe,
    choice
  };
}
// GROUNDED reflection generator (post-details, full reveal)
function generateGroundedReflection(input: string, birthDetails: {
  name: string;
  dateOfBirth: string;
  timeOfBirth: string;
  placeOfBirth: string;
}) {
  const words = input.toLowerCase();
  const hasWork = words.includes('work') || words.includes('job') || words.includes('career');
  const hasRelationship = words.includes('relationship') || words.includes('partner') || words.includes('friend');
  const hasDecision = words.includes('decision') || words.includes('choose') || words.includes('decide');
  const hasTime = words.includes('time') || words.includes('busy') || words.includes('waiting');
  const hasEnergy = words.includes('tired') || words.includes('energy') || words.includes('exhausted');
  let noticing: string[];
  let pattern: string[];
  let reframe: string;
  let choice: string[];
  if (hasWork) {
    noticing = ["Right now, something at work isn't landing the way it used to.", "In this period, there's effort happening but the return feels unclear.", 'At this point, the rhythm has shifted without obvious cause.'];
    pattern = ['This phase tends to bring a disconnection between output and meaning.', 'During periods like this, the doing continues but orientation quietly drifts.', 'This window often creates a gap where effort feels real but direction feels borrowed.'];
    reframe = "This isn't burnout — it's a phase where effort temporarily outruns orientation. The mismatch is time-specific, not structural.";
    choice = ["This period doesn't demand urgency.", 'Waiting, observing, or acting deliberately are all coherent responses here.', 'Nothing here forces a decision.'];
  } else if (hasRelationship) {
    noticing = ["Right now, something in how you're relating has become slightly misaligned.", 'In this period, the connection is still there but the ease has shifted.', "At this point, there's a gap between what's said and what's felt."];
    pattern = ['This phase tends to bring a tension between closeness and clarity.', 'During periods like this, wanting both often means neither arrives cleanly.', 'This window often creates a temporary mismatch between presence and precision.'];
    reframe = "This isn't distance — it's a period where clarity arrives slowly, not decisively. The gap is phase-specific.";
    choice = ["This period doesn't demand resolution.", 'Naming it, sitting with it, or letting it pass are all coherent responses here.', 'Nothing here forces a conversation.'];
  } else if (hasDecision) {
    noticing = ['Right now, a decision is present but not pressing.', 'In this period, multiple directions feel equally valid.', 'At this point, the weight of choosing has become heavier than the choice itself.'];
    pattern = ['This phase tends to bring suspended motion — not indecision, but re-evaluation.', 'During periods like this, clarity often arrives after the pressure to choose has passed.', 'This window often creates a loop where landing anywhere feels premature.'];
    reframe = "This isn't indecision — it's a pause that tends to appear before direction stabilizes. The suspension is time-specific.";
    choice = ["This period doesn't demand a decision.", 'Waiting, exploring, or choosing provisionally are all coherent responses here.', 'Nothing here forces resolution.'];
  } else if (hasTime || hasEnergy) {
    noticing = ['Right now, time is passing but not in the way you expected.', "In this period, there's a sense of waiting without knowing what for.", 'At this point, energy is being spent but not replenished in kind.'];
    pattern = ['This phase tends to bring a feeling that presence and progress are mutually exclusive.', 'During periods like this, being fully here can feel like falling behind.', 'This window often creates a mismatch between effort and return.'];
    reframe = "This isn't stagnation — it's a period where motion and traction temporarily separate. The gap is phase-specific, not permanent.";
    choice = ["This period doesn't demand acceleration.", 'Resting, continuing, or recalibrating are all coherent responses here.', 'Nothing here forces a pace.'];
  } else {
    noticing = ['Right now, something is slightly off but not urgently wrong.', "In this period, there's a quiet dissonance between what is and what feels right.", 'At this point, the situation is familiar but the feeling has shifted.'];
    pattern = ['This phase tends to bring a gap between how things appear and how they land.', 'During periods like this, the surface stays stable while something underneath keeps moving.', 'This window often creates a sense that the familiar no longer fits the same way.'];
    reframe = "This isn't confusion — it's a period where clarity arrives slowly, not decisively. The dissonance is time-specific.";
    choice = ["This period doesn't demand clarity.", 'Observing, adjusting, or simply waiting are all coherent responses here.', 'Nothing here forces understanding.'];
  }
  return {
    noticing,
    pattern,
    reframe,
    choice
  };
}